import { Prisma } from "@prisma/client";
import { BuyableItemHandler, BuyableItemOrganiser } from "../buyable-item.types";

type PartialAiImageCreditsBuyableItem = {
  aiImageCreditsOrganiser?: { id: string } | null;
};

type AiImageCreditsBuyableItem = {
  aiImageCreditsOrganiser: { id: string };
};

type AiImageCreditsWithOrganiser = {
  aiImageCreditsOrganiser: BuyableItemOrganiser;
};

const GROUP_KEY_PREFIX = "aiImageCredits";

export const AiImageCreditsBuyableItemHandler = {
  displayName: "Crédit génération image IA",
  isCumulative: true,
  accounting: {
    generalAccountCode: "706127",
    invoiceLabel: "Crédit génération image IA",
  },

  getOrganiser(item: AiImageCreditsWithOrganiser): BuyableItemOrganiser {
    return item.aiImageCreditsOrganiser;
  },

  isValid(item: PartialAiImageCreditsBuyableItem): boolean {
    return !!item.aiImageCreditsOrganiser?.id;
  },

  findEquivalent(
    item: PartialAiImageCreditsBuyableItem,
  ): Prisma.BuyableItemWhereInput {
    return {
      aiImageCreditsOrganiser: {
        id: item.aiImageCreditsOrganiser?.id,
      },
    };
  },

  create({
    aiImageCreditsOrganiser,
  }: AiImageCreditsBuyableItem): Omit<Prisma.BuyableItemCreateInput, "buyableType"> {
    return {
      aiImageCreditsOrganiser: {
        connect: {
          id: aiImageCreditsOrganiser.id,
        },
      },
    };
  },

  parseGroupKeyAsWhere(groupKey: string): Prisma.BuyableItemWhereInput {
    if (!AiImageCreditsBuyableItemHandler.isGroupKey(groupKey)) {
      throw new Error(`Invalid group key for AI image credits: ${groupKey}`);
    }

    const [, organiserId] = groupKey.split("|");
    return {
      aiImageCreditsOrganiser: {
        id: organiserId,
      },
    };
  },

  isGroupKey(groupKey: string): boolean {
    return groupKey.startsWith(`${GROUP_KEY_PREFIX}|`);
  },

  getGroupKey(item: PartialAiImageCreditsBuyableItem): string {
    const organiserId = item.aiImageCreditsOrganiser?.id;
    if (!organiserId) {
      throw new Error("Cannot get group key for invalid aiImageCredits item");
    }
    return `${GROUP_KEY_PREFIX}|${organiserId}`;
  },

  getPricingQuery(): Prisma.PricingFindFirstArgs {
    return {
      where: {
        backofficePrestation: {
          type: "aiImageCredits",
        },
      },
    };
  },
} as const satisfies BuyableItemHandler;
